import { assignInlineVars } from '@vanilla-extract/dynamic'
import * as styles from './scale-bar.css.ts'

export type ScaleBarProps = {
  /**
   * The band colours in reading order, low end first. Two or more.
   *
   * Taken from the consumer rather than defined here on purpose: the point of this bar is to show the
   * colours a reader will actually meet on that screen, and the two scales in this product do not share
   * a ramp — risk uses the brand palette, posture uses severity plus the product's green. A ramp defined
   * here would be a third one, matching neither.
   */
  stops: readonly string[]
  /**
   * The band names, low first, spread evenly beneath the ramp.
   *
   * **Usually one per stop**, which is what makes the bar a key: a reader who meets a "Fair" tag in a
   * table can find it here and see which colour it is. Two labels is also valid and gives ends only —
   * enough to state a direction for a scale whose middle bands have no names worth printing.
   */
  labels: readonly string[]
}

/**
 * A scale as a colour ramp with its bands named beneath it.
 *
 * Exists because this product shows two scores that run in opposite directions, and prose saying which way
 * is up is the part readers skip. Named bands under a ramp answer that at a glance, and double as a key to
 * the colours the scores themselves are drawn in.
 *
 * It plots no value and takes none — a marker would make it a chart of one number, and both places it
 * appears are explaining a scale rather than reporting a score.
 *
 * **The fill is continuous while the bands are stepped**, at cutoffs this does not draw. Deliberate: the
 * bar teaches the vocabulary and its direction, and discrete blocks would invite reading boundaries off
 * their edges — which are evenly spaced here and are not evenly spaced in the scale.
 */
export const ScaleBar: React.FC<ScaleBarProps> = ({ stops, labels }) => (
  <div className={styles.scaleBar}>
    <div
      className={styles.track}
      aria-hidden
      style={assignInlineVars({ [styles.ramp]: `linear-gradient(to right, ${stops.join(', ')})` })}
    />
    <p className={styles.labelRow}>
      {labels.map(label => (
        <span key={label}>{label}</span>
      ))}
    </p>
  </div>
)
